import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, eur } from "../lib/api";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";

const EASE = [0.16, 1, 0.3, 1];

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="product-not-found">
        <p className="text-muted-foreground">Questo pezzo non esiste più nella collezione.</p>
        <Link to="/shop" className="text-primary text-sm mt-4 inline-block">
          Torna alla collezione
        </Link>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="product-loading">
        <p className="text-muted-foreground tracking-wide">Caricamento…</p>
      </main>
    );
  }

  return (
    <main className="pt-20" data-testid="product-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-10 md:pt-16">
        <Link
          to="/shop"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Collezione
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE }}
            className="lg:sticky lg:top-28"
          >
            <div className="overflow-hidden border border-white/10 h-[60vh] lg:h-[78vh] min-h-[380px]">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="lg:pt-8"
          >
            <p className="overline-label mb-6">{product.category}</p>
            <h1
              className="font-serif font-light text-5xl md:text-7xl leading-[0.98] mb-8"
              data-testid="product-name"
            >
              {product.name}
            </h1>
            <p className="font-serif text-3xl text-primary mb-10" data-testid="product-price">
              {eur(product.price)}
            </p>
            <p className="text-muted-foreground leading-relaxed tracking-wide max-w-lg mb-12" data-testid="product-description">
              {product.description}
            </p>

            <div className="space-y-0 border-t border-white/10 mb-12 max-w-lg">
              {product.material && (
                <div className="flex justify-between gap-6 py-5 border-b border-white/10">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Materiale</span>
                  <span className="text-sm text-right">{product.material}</span>
                </div>
              )}
              {product.dimensions && (
                <div className="flex justify-between gap-6 py-5 border-b border-white/10">
                  <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Dimensioni</span>
                  <span className="text-sm text-right">{product.dimensions}</span>
                </div>
              )}
              <div className="flex justify-between gap-6 py-5 border-b border-white/10">
                <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Disponibilità</span>
                <span className="text-sm text-right" data-testid="product-stock">
                  {(product.stock ?? 0) > 0 ? `${product.stock} pezzi` : "Esaurito"}
                </span>
              </div>
              <div className="flex justify-between gap-6 py-5 border-b border-white/10">
                <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Spedizione</span>
                <span className="text-sm text-right">24/48h · Reso entro 15 giorni</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-5">
              <div className="flex items-center border border-white/15 rounded-full">
                <button
                  data-testid="qty-decrease"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-14 w-12 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Diminuisci quantità"
                >
                  <Minus size={15} />
                </button>
                <span className="w-8 text-center text-sm" data-testid="qty-value">{qty}</span>
                <button
                  data-testid="qty-increase"
                  onClick={() => setQty((q) => Math.min(Math.min(product.stock ?? 20, 20), q + 1))}
                  className="h-14 w-12 flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Aumenta quantità"
                >
                  <Plus size={15} />
                </button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                disabled={(product.stock ?? 1) <= 0}
                onClick={() => {
                  addItem(product, qty);
                  toast.success(`${product.name} aggiunto al carrello`);
                }}
                className="inline-flex items-center gap-3 h-14 px-10 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-primary"
              >
                <ShoppingBag size={16} strokeWidth={1.5} />
                {(product.stock ?? 1) <= 0 ? "Esaurito" : "Aggiungi al carrello"}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
